import React, { useState, useRef, useEffect } from 'react';
import { variables, Box, breakpoints, Button, Header, space, Spinner } from '@generationtux/component-library';
import styled, { css } from 'styled-components';
import PreviewLayer from './PreviewLayer';
import { previewLayerStyles } from './previewLayerStyles';
import { bundles } from '../../../data/bundles';
import { products } from '../../../data/products';
import { disableBodyScroll, enableBodyScroll, clearAllBodyScrollLocks } from 'body-scroll-lock';
import { useSpring, animated } from 'react-spring';
import { scrollTop } from '../../../utils/utils';

const ButtonIcon = styled(Button)`
  flex-direction: column;
  position: absolute;
  top: ${props => (props.prevExpanded ? '25%' : '50%')};
  ${props =>
    !props.prevExpanded &&
    props.sideCarCategory &&
    !props.mediaQueryXsMatches &&
    css`
      display: none;
    `}
  &.undo {
    left: ${props => (props.mediaQueryXsMatches ? 0 : `${space[3]}px`)};
  }
  &.redo {
    right: ${space[3]}px;
  }
  @media (min-width: ${breakpoints['xs']}) {
    &.undo {
      left: 0;
    }
    &.redo {
      right: ${space[2]}px;
    }
  }
  @media (min-width: ${breakpoints['sm']}) {
    &.undo {
      left: ${props => (props.mediaQueryXsMatches ? 0 : `${space[3]}px`)};
    }
    &.redo {
      right: ${space[3]}px;
    }
  }
  @media (min-width: ${breakpoints['lg']}) {
    &.redo {
      right: ${space[4]}px;
    }
  }
`;

const PreviewWrapper = styled.div`
  transform-origin: right top;
  transform: scale(1) translateZ(1px);
  padding: ${space[3]}px;
  pointer-events: auto;
  will-change: transform;
  text-align: center;
  display: inline-block;

  ${props =>
    props.activeCollection &&
    `
    background-color:white;
    position: fixed;
    z-index: 500;
    top: 0;
    left: 0;
    right: 0;
    overflow: hidden;
    box-shadow: 0 -20px 40px rgba(0, 0, 0, 0.5);
  `};

  @media (min-width: ${breakpoints['xs']}) {
    padding: 0;
    background: transparent;
    position: relative;
    z-index: 1000;
    top: 0;
    left: 0;
    right: 0;
    height: auto;
    overflow: visible;
    box-shadow: none;
  }
`;

const PreviewImageWrapper = styled.div`
  position: relative;
  transform-origin: center center;
  transition: all .2s ease;
  :active {
    transform: scale(.97);
  }

  ${previewLayerStyles}

  .preview-illustrated {
    opacity: .6;
  }

  button {
    position: absolute;
    z-index: 1500;
    top: 40%;
    left: 50%;
    transform: translate(-50%,0%);
    opacity: ${props => (props.previewHasItems ? 0 : 1)};
  }

  img {
    max-width: calc(100% - 60px);
    max-height: ${props => (props.activeCollection ? '70vh' : '65vh')};
    @media (min-width: ${breakpoints['xs']}) {
      max-height: 70vh;
    }
  }

  .loading-indicator {
    content:"";
    width: 80px;
    height: 80px;
    background-color: ${props => variables(props).grayLight}ee;
    opacity: 0;
    position: absolute;
    top: 30%;
    left: 50%;
    transform: translate(-50%,-50%) scale(.75);
    z-index: 2000;
    transition: .2s ease .2s;
  }
  .loading ~ .loading-indicator {
    opacity: 1;
    transition-delay; 0;
  }

  @media(hover:hover){
    &:hover {
      cursor: pointer;
      button {
        transform: translate(-50%,${props => (props.previewHasItems ? -50 : 0)}%);
        opacity: 1;
      }
      .preview-illustrated {
        opacity: 1;
      }
    }
  }

  @media (min-width: ${breakpoints['xs']}) {
    transition: transform .2s ease, filter .2s ease;
    transform: translateY(0) scale(1);
    transform-origin: center center;
  }

`;

const PreviewExpandedBox = styled(Box)`
  position: absolute;
  left: 0;
  right: 0;
`;

const PreviewHeader = styled(Header)`
  display: none;
  ${props =>
    props.activeCollection &&
    `
    display: block;
    position: absolute;
    left: 0;
    right: 0;
    top: 50%;
    transform: translateY(${props.prevExpanded ? '25vh' : '-50%'});
    text-align: center;
  `};
  @media (min-width: ${breakpoints['xs']}) {
    display: none;
  }
`;

const AnimatedPreviewWrapper = animated(PreviewWrapper);

const Preview = ({
  selectedCatalogNumbers,
  undo,
  redo,
  isUndoable,
  isRedoable,
  activeCollection,
  previewExploded,
  setPreviewExploded,
  mediaQueryXsMatches,
  ...props
}) => {
  const [prevExpanded, setPrevExapnded] = useState(false);

  const AnimationPropsPreviewWrapper = useSpring({
    config: { tension: 200 },
    height: prevExpanded ? '100vh' : '9vh',
  });
  const AnimationPropsPreviewImageWrapper = useSpring({
    config: { tension: 200 },
    transformOrigin: 'top right',
    transform: `scale(${prevExpanded ? 1.0 : 0.4}) translateY(${prevExpanded ? 0 : -24}px)`,
  });

  const targetElement = document.querySelector('#body');
  const compiledImages = useRef(null);
  const togglePreviewExpanded = () => {
    if (!mediaQueryXsMatches && activeCollection !== null) {
      if (prevExpanded) {
        setPrevExapnded(false);
        enableBodyScroll(targetElement);
      } else {
        disableBodyScroll(targetElement);
        setPrevExapnded(true);
      }
    }
  };

  useEffect(() => {
    return () => {
      clearAllBodyScrollLocks();
    };
  }, []);

  const productLayers = products
    .filter(product => {
      if (
        selectedCatalogNumbers
          .filter(selectedItem => !selectedItem.isBundle)
          .map(selectedItem => selectedItem.activeItem)
          .indexOf(product.catalogNumber) > -1
      ) {
        return true;
      } else {
        return false;
      }
    })
    .concat(
      bundles
        .filter(bundle => {
          if (bundle.displayName.includes('RECOMMENDED') > 0) {
            return false;
          }
          return true;
        })
        .reduce((acc, bundle, index) => {
          bundle.products.map(product => {
            if (
              selectedCatalogNumbers.map(selectedItem => selectedItem.activeItem).indexOf(product.catalogNumber) > -1
            ) {
              // map bundle products
              bundle.products.forEach(bundleProduct => {
                bundleProduct.type = bundleProduct.type.includes('Pant') ? 'Pants' : 'Jacket';
                bundleProduct.costToCustomer = bundle.cost;
                acc.push(bundleProduct);
              });
            }
            return acc;
          });
          return acc;
        }, [])
    );

  return (
    <AnimatedPreviewWrapper
      style={!mediaQueryXsMatches && activeCollection ? AnimationPropsPreviewWrapper : null}
      ref={compiledImages}
      prevExpanded={prevExpanded}
      activeCollection={activeCollection}
      {...props}
    >
      <PreviewHeader prevExpanded={prevExpanded} activeCollection={activeCollection} type={6} textColor="textPrimary">
        <Box as="span" display="flex" alignItems="center" px={3}>
          {!prevExpanded && (
            <Button
              buttonType="default"
              outline
              size="sm"
              buttonIcon="TriangleDown"
              buttonIconPosition="right"
              onClick={() => {
                togglePreviewExpanded();
              }}
            >
              Zoom In
            </Button>
          )}
        </Box>
      </PreviewHeader>
      <animated.div style={!mediaQueryXsMatches && activeCollection ? AnimationPropsPreviewImageWrapper : null}>
        <PreviewImageWrapper
          activeCollection={activeCollection}
          prevExpanded={prevExpanded}
          previewExploded={previewExploded}
          previewHasItems={productLayers.length > 0}
          onClick={() => {
            setPreviewExploded(!previewExploded);
            scrollTop();
          }}
          className={`preview-image-wrapper ${productLayers
            .map(product => product.type.toLowerCase().replace(',', '-'))
            .join(' ')}`}
        >
          <Button outline>Customize</Button>
          <img src="https://gentux.imgix.net/1584373843_placeholder.png" alt="placeholder" className="placeholder" />
          {productLayers.length > 0 ? (
            <>
              {productLayers.map(product => (
                <PreviewLayer key={product.id} activeCollection={activeCollection} product={product} />
              ))}
            </>
          ) : (
            <Box>
              <img
                src="https://gentux.imgix.net/1591704711_200608-151436-all.png"
                alt="empty look"
                className="preview-illustrated"
              />
            </Box>
          )}
          <div className="loading-indicator">
            <Spinner type="minimal" />
          </div>
        </PreviewImageWrapper>
      </animated.div>

      {(isUndoable || isRedoable) && (
        <>
          <ButtonIcon
            buttonType="link"
            className="undo"
            disabled={!isUndoable}
            onClick={() => undo()}
            size="sm"
            buttonIcon="Undo"
            px={2}
            prevExpanded={prevExpanded}
            sideCarCategory={activeCollection}
            mediaQueryXsMatches={mediaQueryXsMatches}
          >
            Undo
          </ButtonIcon>

          <ButtonIcon
            outline
            buttonType="link"
            className="redo"
            disabled={!isRedoable}
            onClick={() => redo()}
            size="sm"
            buttonIcon="Redo"
            px={2}
            prevExpanded={prevExpanded}
            sideCarCategory={activeCollection}
            mediaQueryXsMatches={mediaQueryXsMatches}
          >
            Redo
          </ButtonIcon>
        </>
      )}

      {prevExpanded && (
        <PreviewExpandedBox display="flex" justifyContent="center" alignItems="center">
          <Button
            outline
            buttonIcon="TriangleUp"
            buttonIconPosition="right"
            onClick={() => {
              togglePreviewExpanded()
            }}
          >
            Zoom Out
          </Button>
        </PreviewExpandedBox>
      )}
    </AnimatedPreviewWrapper>
  );
};

export default Preview;
